import { CamundaBaseService } from './base-service';
import type {
  CategorizedProcesses,
  ProcessDefinition,
  ProcessInstance,
  TaskFormVariables,
} from './types';
import { categorizeProcesses, filterStartableProcessDefinitions } from '@/lib/process-registry';
import { encodeCamundaPathSegment } from './process-definition-id';

interface StartProcessRequestBody {
  variables: TaskFormVariables;
  businessKey?: string;
}

export class CamundaProcessService extends CamundaBaseService {
  static async getProcessDefinitions(): Promise<ProcessDefinition[]> {
    return filterStartableProcessDefinitions(
      await this.makeRequest<ProcessDefinition[]>(
        '/process-definition?startableInTasklist=true&latestVersion=true'
      ),
    );
  }

  static async getProcessesByCategory(categoryPrefix: string): Promise<ProcessDefinition[]> {
    try {
      const params = new URLSearchParams({
        keyLike: `${categoryPrefix}-%`,
        startableInTasklist: 'true',
        latestVersion: 'true'
      });

      return filterStartableProcessDefinitions(
        await this.makeRequest<ProcessDefinition[]>(`/process-definition?${params}`),
      );
    } catch (error) {
      console.error(`Error fetching processes for category ${categoryPrefix}:`, error);
      return [];
    }
  }

  static async getCategorizedProcesses(): Promise<CategorizedProcesses> {
    try {
      const allProcesses = await this.getProcessDefinitions();
      return categorizeProcesses(allProcesses);
    } catch (error) {
      console.error('Error categorizing processes:', error);
      return { 'Sin Categoría': [] };
    }
  }

  static async startProcess(processKey: string, variables?: TaskFormVariables): Promise<ProcessInstance> {
    try {
      const requestBody: StartProcessRequestBody = {
        variables: variables ?? {}
      };

      return await this.makeRequest<ProcessInstance>(`/process-definition/key/${processKey}/start`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });
    } catch (error) {
      console.error('Error starting process:', error);
      throw error;
    }
  }

  static async getProcessDefinition(processDefinitionId: string): Promise<ProcessDefinition | null> {
    try {
      const encodedProcessDefinitionId = encodeCamundaPathSegment(processDefinitionId);
      return await this.makeRequest<ProcessDefinition>(`/process-definition/${encodedProcessDefinitionId}`);
    } catch (error) {
      console.error('Error fetching process definition:', error);
      return null;
    }
  }

  static async getLatestProcessDefinitionByKey(processDefinitionKey: string): Promise<ProcessDefinition> {
    const encodedKey = encodeURIComponent(processDefinitionKey);
    return this.makeRequest<ProcessDefinition>(`/process-definition/key/${encodedKey}`);
  }

  static async getProcessInstances(processDefinitionKey?: string): Promise<ProcessInstance[]> {
    try {
      const params = new URLSearchParams();
      if (processDefinitionKey) {
        params.append('processDefinitionKey', processDefinitionKey);
      }
      
      return await this.makeRequest<ProcessInstance[]>(`/process-instance?${params}`);
    } catch (error) {
      console.error('Error fetching process instances:', error);
      return [];
    }
  }

  static async cancelProcess(processInstanceId: string, userId: string): Promise<void> {
    console.log("Llamando DELETE a Camunda con", processInstanceId, userId);
    try {
      return await this.makeRequest(`/process-instance/${processInstanceId}`, {
        method: 'DELETE',
        body: JSON.stringify({ userId }), 
      });
    } catch (error) {
      console.error('Error canceling process instance:', error);
      throw error;
    }
  }

static async startAnulacionProcess(
  processKey: string,
  incidenciaIdToAnulate: string,
  processInstanceIdToAnulate: string,
  extraVariables?: TaskFormVariables
): Promise<ProcessInstance> {
  const historicaValue = JSON.stringify({
    id: incidenciaIdToAnulate,
    id_instancia_proceso: processInstanceIdToAnulate,
  });

  const variables: TaskFormVariables = {
    historica: {
      value: historicaValue,
      type: 'Object',
      valueInfo: {
        objectTypeName: 'java.util.HashMap',
        serializationDataFormat: 'application/json',
      },
    },
  };
  if (extraVariables) {
    Object.assign(variables, extraVariables);
  }

  return this.startProcess(processKey, variables);
}

}
